// src/Components/Chart_Support_Files/chartReloadUtils.js

import { getStoreKey } from './chartSymbolUtils';
import { getGreekToIndicatorMap, indexIndicators } from './chartIndicatorConfig';

export function hasStrikePriceChanged(newData, prevData, type) {
  if (!prevData || !prevData.length) {
    return { changed: false, changedGreeks: [] };
  }

  if (!newData || !newData.length) {
    return { changed: false, changedGreeks: [] };
  }

  if (type === 'dynamicLevels') {
    const prevLevels = {};

    prevData.forEach((level) => {
      prevLevels[level.level_type] = level.strike_price;
    });

    return {
      changed: newData.some(
        (level) => prevLevels[level.level_type] !== level.strike_price
      ),
      changedGreeks: [],
    };
  }

  if (type === 'topOptions') {
    const greekTypes = [...new Set(newData.map((opt) => opt.greek_type))];
    const changedGreeks = [];

    greekTypes.forEach((greekType) => {
      const prevGreekData = prevData.filter(
        (opt) =>
          opt.greek_type === greekType &&
          (opt.rank === 1 || opt.rank === 2)
      );

      const newGreekData = newData.filter(
        (opt) =>
          opt.greek_type === greekType &&
          (opt.rank === 1 || opt.rank === 2)
      );

      const optionTypes = ['call', 'put'];

      const hasGreekChanged = optionTypes.some((optType) => {
        const prevStrikes = prevGreekData
          .filter((opt) => opt.option_type === optType)
          .reduce((acc, opt) => {
            acc[opt.rank] = opt.strike;
            return acc;
          }, {});

        const newStrikes = newGreekData
          .filter((opt) => opt.option_type === optType)
          .reduce((acc, opt) => {
            acc[opt.rank] = opt.strike;
            return acc;
          }, {});

        return [1, 2].some((rank) => prevStrikes[rank] !== newStrikes[rank]);
      });

      if (hasGreekChanged) {
        changedGreeks.push(greekType);
      }
    });

    return {
      changed: changedGreeks.length > 0,
      changedGreeks,
    };
  }

  return { changed: false, changedGreeks: [] };
}

export function reloadIndicators({
  chart,
  currentSymbol,
  isDynamicLevelsChanged,
  isTopOptionsChanged,
  changedGreeks = [],
  dynamicLevels,
  topOptions,
}) {
  if (!chart || !currentSymbol) return;

  const config = indexIndicators[currentSymbol];
  if (!config) return;

  const existingStudyNames = chart.getAllStudies().map((study) => study.name);
  const indicatorsToReload = [];

  if (isDynamicLevelsChanged) {
    const dynamicLevelName = `${currentSymbol} Daily Dynamic Levels`;

    if (existingStudyNames.includes(dynamicLevelName)) {
      indicatorsToReload.push(dynamicLevelName);
    }
  }

  if (isTopOptionsChanged && changedGreeks.length) {
    const greekToIndicatorMap = getGreekToIndicatorMap(currentSymbol);

    changedGreeks.forEach((greekType) => {
      const indicatorName = greekToIndicatorMap[greekType];

      if (existingStudyNames.includes(indicatorName)) {
        indicatorsToReload.push(indicatorName);
      }
    });
  }

  chart.getAllStudies().forEach((study) => {
    if (indicatorsToReload.includes(study.name)) {
      chart.removeEntity(study.id);
    }
  });

  indicatorsToReload.forEach((indicator) => {
    chart.createStudy(indicator, false, false, {
      dynamicLevels,
      topOptions,
    });
  });
}

export function handleIndicatorReloadCheck({
  chart,
  dynamicLevels,
  topOptions,
  prevDynamicLevelsRef,
  prevTopOptionsRef,
}) {
  if (!chart) return;

  const currentSymbol = chart.symbol();
  const storeKey = getStoreKey(currentSymbol);

  if (!storeKey) {
    prevDynamicLevelsRef.current = {};
    prevTopOptionsRef.current = {};
    return;
  }

  const currentDynamicArray = dynamicLevels[storeKey] || [];
  const currentTopOptionsArray = topOptions[storeKey] || [];

  const prevDynamicArray = prevDynamicLevelsRef.current?.[storeKey] || [];
  const prevTopOptionsArray = prevTopOptionsRef.current?.[storeKey] || [];

  const dynamicLevelsResult = hasStrikePriceChanged(
    currentDynamicArray,
    prevDynamicArray,
    'dynamicLevels'
  );

  const topOptionsResult = hasStrikePriceChanged(
    currentTopOptionsArray,
    prevTopOptionsArray,
    'topOptions'
  );

  if (dynamicLevelsResult.changed || topOptionsResult.changed) {
    reloadIndicators({
      chart,
      currentSymbol,
      isDynamicLevelsChanged: dynamicLevelsResult.changed,
      isTopOptionsChanged: topOptionsResult.changed,
      changedGreeks: topOptionsResult.changedGreeks,
      dynamicLevels,
      topOptions,
    });
  }

  prevDynamicLevelsRef.current = {
    ...prevDynamicLevelsRef.current,
    [storeKey]: currentDynamicArray,
  };

  prevTopOptionsRef.current = {
    ...prevTopOptionsRef.current,
    [storeKey]: currentTopOptionsArray,
  };
}